import { useState, useCallback, useRef, useEffect } from "react";
import { useExperimentStore } from "../../store/experimentStore";
import { experimentSchema } from "../../validation/experimentSchema";
import { JsonPreview } from "../Json/JsonPreview";
import { RuleEditor } from "../Rules/RuleEditor";
import { VariationEditor } from "../Variations/VariationEditor";
import {
  ExperimentType,
  ExperimentUnitType,
  Phase,
  Surface,
  AssignmentMode,
  ExposureTracking,
  EligibilityPersistence,
} from "../../types/experiment";
import { EnumSelect } from "../Shared/EnumSelect";
import { SurfaceMultiSelect } from "../Shared/SurfaceMultiSelect";
import { TimestampEditor } from "../Shared/TimestampEditor";
import { SnowflakeInput } from "../Shared/SnowflakeInput";
import { TagInput } from "../Shared/TagInput";
import { DebugConfigEditor } from "../Shared/DebugConfigEditor";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { Separator } from "../ui/separator";
import {
  AlertCircle,
  Layers,
  Variable,
  Settings,
  FileJson,
  Plus,
  FlaskConical,
} from "lucide-react";

const enumOpts = (e: object) =>
  Object.entries(e as Record<string, number>)
    .filter(([k]) => isNaN(Number(k)))
    .map(([key, value]) => ({ value: value as number, label: key }));

const experimentTypeOptions = enumOpts(ExperimentType);
const unitTypeOptions = enumOpts(ExperimentUnitType);
const phaseOptions = enumOpts(Phase);
const assignmentModeOptions = enumOpts(AssignmentMode);
const exposureTrackingOptions = enumOpts(ExposureTracking);
const eligibilityPersistenceOptions = enumOpts(EligibilityPersistence);

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[11px] font-semibold uppercase tracking-wider text-overlay1 mb-2">
      {children}
    </h3>
  );
}

function EmptyState({ icon: Icon, title, description }: { icon: React.ComponentType<{ className?: string }>; title: string; description: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="rounded-full bg-surface0 p-3 mb-3">
        <Icon className="h-6 w-6 text-overlay1" />
      </div>
      <p className="text-sm font-medium text-subtext0">{title}</p>
      <p className="text-xs text-overlay0 mt-1">{description}</p>
    </div>
  );
}

export function ExperimentEditor() {
  const {
    experiment,
    errors,
    setExperiment,
    setErrors,
    reinitialize,
    updateField,
    addRule,
    removeRule,
    updateRule,
    addVariation,
    removeVariation,
    updateVariation,
  } = useExperimentStore();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState("basic");
  const [jsonPanelWidth, setJsonPanelWidth] = useState(420);
  const [isResizing, setIsResizing] = useState(false);
  const [showJson, setShowJson] = useState(true);

  useEffect(() => {
    if (isResizing) {
      const handleMouseMove = (e: MouseEvent) => {
        setJsonPanelWidth(Math.max(300, Math.min(window.innerWidth - 400, window.innerWidth - e.clientX)));
      };
      const handleMouseUp = () => setIsResizing(false);
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isResizing]);

  const validate = useCallback(() => {
    const result = experimentSchema.safeParse(experiment);
    if (!result.success) {
      const formatted: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const path = issue.path.join(".");
        formatted[path] = issue.message;
      }
      setErrors(formatted);
      return false;
    }
    setErrors({});
    return true;
  }, [experiment, setErrors]);

  const handleImport = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const parsed = JSON.parse(ev.target?.result as string);
          setExperiment(parsed);
        } catch {
          setErrors({ import: "Invalid JSON file" });
        }
      };
      reader.readAsText(file);
      e.target.value = "";
    },
    [setExperiment, setErrors],
  );

  const errorCount = Object.keys(errors).length;

  const tabs = [
    { id: "basic", label: "Basic Info", icon: FileJson },
    { id: "config", label: "Configuration", icon: Settings },
    { id: "variations", label: "Variations", icon: Variable, count: experiment.variations.length },
    { id: "rules", label: "Rules", icon: Layers, count: experiment.rules.length },
  ];

  const renderBasicInfo = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <SectionHeader>Identity</SectionHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="mb-1.5 block text-xs text-overlay1">ID</Label>
            <Input
              value={experiment.id}
              onChange={(e) => updateField("id", e.target.value)}
              placeholder="experiment_id"
            />
            {errors["id"] && <p className="text-xs text-red mt-1">{errors["id"]}</p>}
          </div>
          <div>
            <Label className="mb-1.5 block text-xs text-overlay1">Name</Label>
            <Input
              value={experiment.name}
              onChange={(e) => updateField("name", e.target.value)}
              placeholder="Experiment name"
            />
          </div>
        </div>
        <div>
          <Label className="mb-1.5 block text-xs text-overlay1">Title</Label>
          <Input
            value={experiment.title}
            onChange={(e) => updateField("title", e.target.value)}
            placeholder="Display title"
          />
        </div>
        <div>
          <Label className="mb-1.5 block text-xs text-overlay1">Description</Label>
          <Textarea
            value={experiment.description}
            onChange={(e) => updateField("description", e.target.value)}
            placeholder="What does this experiment test?"
            rows={2}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="mb-1.5 block text-xs text-overlay1">Hypothesis</Label>
            <Textarea
              value={experiment.hypothesis}
              onChange={(e) => updateField("hypothesis", e.target.value)}
              placeholder="What do you expect to happen?"
              rows={2}
            />
          </div>
          <div>
            <Label className="mb-1.5 block text-xs text-overlay1">Tech Spec Link</Label>
            <Input
              value={experiment.tech_spec_link}
              onChange={(e) => updateField("tech_spec_link", e.target.value)}
              placeholder="https://..."
            />
          </div>
        </div>
      </div>

      <Separator />

      <div className="space-y-4">
        <SectionHeader>Ownership</SectionHeader>
        <div className="grid grid-cols-2 gap-3">
          <SnowflakeInput
            label="Creator ID"
            value={experiment.creator_id}
            onChange={(v) => updateField("creator_id", v)}
          />
          <SnowflakeInput
            label="Editor ID"
            value={experiment.editor_id}
            onChange={(v) => updateField("editor_id", v)}
          />
        </div>
      </div>

      <Separator />

      <div className="space-y-4">
        <SectionHeader>Versioning</SectionHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="mb-1.5 block text-xs text-overlay1">Version</Label>
            <Input
              type="number"
              value={experiment.version}
              onChange={(e) => updateField("version", parseInt(e.target.value) || 1)}
            />
          </div>
          <div>
            <Label className="mb-1.5 block text-xs text-overlay1">Revision</Label>
            <Input
              type="number"
              value={experiment.revision}
              onChange={(e) => updateField("revision", parseInt(e.target.value) || 1)}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="mb-1.5 block text-xs text-overlay1">Hash Key</Label>
            <Input
              value={experiment.hash_key}
              onChange={(e) => updateField("hash_key", e.target.value)}
              placeholder="hash_key"
            />
            {errors["hash_key"] && <p className="text-xs text-red mt-1">{errors["hash_key"]}</p>}
          </div>
          <EnumSelect
            label="Unit Type"
            value={experiment.unit_type}
            options={unitTypeOptions}
            onChange={(v) => updateField("unit_type", v)}
          />
        </div>
      </div>

      <Separator />

      <div className="space-y-4">
        <SectionHeader>Timestamps</SectionHeader>
        <div className="grid grid-cols-2 gap-3">
          <TimestampEditor
            label="Created At"
            value={experiment.created_at}
            onChange={(v) => updateField("created_at", v)}
          />
          <TimestampEditor
            label="Edited At"
            value={experiment.edited_at}
            onChange={(v) => updateField("edited_at", v)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <TimestampEditor
            label="Expected End Date"
            value={experiment.expected_end_date}
            onChange={(v) => updateField("expected_end_date", v)}
          />
          <TimestampEditor
            label="Archive At"
            value={experiment.archive_at}
            onChange={(v) => updateField("archive_at", v)}
          />
        </div>
      </div>
    </div>
  );

  const renderConfig = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <SectionHeader>Experiment Settings</SectionHeader>
        <div className="grid grid-cols-3 gap-3">
          <EnumSelect
            label="Type"
            value={experiment.type}
            options={experimentTypeOptions}
            onChange={(v) => updateField("type", v)}
          />
          <EnumSelect
            label="Phase"
            value={experiment.phase}
            options={phaseOptions}
            onChange={(v) => updateField("phase", v)}
          />
          <EnumSelect
            label="Unit Type"
            value={experiment.unit_type}
            options={unitTypeOptions}
            onChange={(v) => updateField("unit_type", v)}
          />
        </div>
        <div>
          <Label className="mb-1.5 block text-xs text-overlay1">Surfaces</Label>
          <SurfaceMultiSelect
            value={experiment.surfaces}
            onChange={(v) => updateField("surfaces", v)}
          />
        </div>
      </div>

      <Separator />

      <div className="space-y-4">
        <SectionHeader>Assignment</SectionHeader>
        <div className="grid grid-cols-3 gap-3">
          <EnumSelect
            label="Assignment Mode"
            value={experiment.assignment_mode}
            options={assignmentModeOptions}
            onChange={(v) => updateField("assignment_mode", v)}
          />
          <EnumSelect
            label="Exposure Tracking"
            value={experiment.exposure_tracking}
            options={exposureTrackingOptions}
            onChange={(v) => updateField("exposure_tracking", v)}
          />
          <EnumSelect
            label="Eligibility Persistence"
            value={experiment.eligibility_persistence}
            options={eligibilityPersistenceOptions}
            onChange={(v) => updateField("eligibility_persistence", v)}
          />
        </div>
      </div>

      <Separator />

      <div className="space-y-4">
        <SectionHeader>Ownership & IDs</SectionHeader>
        <div className="grid grid-cols-2 gap-3">
          <SnowflakeInput
            label="Owning Team ID"
            value={experiment.owning_team_id}
            onChange={(v) => updateField("owning_team_id", v)}
          />
          <SnowflakeInput
            label="Notification Channel ID"
            value={experiment.cached_notification_channel_id}
            onChange={(v) => updateField("cached_notification_channel_id", v)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="mb-1.5 block text-xs text-overlay1">Winning Variation ID</Label>
            <Input
              type="number"
              value={experiment.winning_variation_id}
              onChange={(e) => updateField("winning_variation_id", parseInt(e.target.value) || 0)}
            />
          </div>
          <div>
            <Label className="mb-1.5 block text-xs text-overlay1">Guild Experiment Version</Label>
            <Input
              type="number"
              value={experiment.guild_experiment_version ?? ""}
              onChange={(e) =>
                updateField("guild_experiment_version", e.target.value ? parseInt(e.target.value) : undefined)
              }
            />
          </div>
        </div>
        <div>
          <Label className="mb-1.5 block text-xs text-overlay1">Custom Unit Prefix</Label>
          <Input
            type="number"
            value={experiment.custom_unit_prefix}
            onChange={(e) => updateField("custom_unit_prefix", parseInt(e.target.value) || 0)}
            className="w-32"
          />
        </div>
      </div>

      <Separator />

      <div className="space-y-4">
        <SectionHeader>Number Line Settings</SectionHeader>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-[10px] text-overlay1 mb-1 block">Mode</Label>
            <Input
              type="number"
              value={experiment.number_line_settings?.mode ?? ""}
              onChange={(e) =>
                updateField("number_line_settings", {
                  mode: parseInt(e.target.value) || 0,
                  linked_id: experiment.number_line_settings?.linked_id ?? "",
                  shared_control: experiment.number_line_settings?.shared_control ?? false,
                })
              }
            />
          </div>
          <div>
            <Label className="text-[10px] text-overlay1 mb-1 block">Linked ID</Label>
            <Input
              value={experiment.number_line_settings?.linked_id ?? ""}
              onChange={(e) =>
                updateField("number_line_settings", {
                  mode: experiment.number_line_settings?.mode ?? 0,
                  linked_id: e.target.value,
                  shared_control: experiment.number_line_settings?.shared_control ?? false,
                })
              }
            />
          </div>
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch
                checked={experiment.number_line_settings?.shared_control ?? false}
                onCheckedChange={(v) =>
                  updateField("number_line_settings", {
                    mode: experiment.number_line_settings?.mode ?? 0,
                    linked_id: experiment.number_line_settings?.linked_id ?? "",
                    shared_control: v,
                  })
                }
              />
              <span className="text-xs text-overlay0">Shared Control</span>
            </label>
          </div>
        </div>
      </div>

      <Separator />

      <div className="space-y-4">
        <SectionHeader>Context & Debug</SectionHeader>
        <div>
          <Label className="mb-1.5 block text-xs text-overlay1">Extra Outcome Context</Label>
          <Input
            value={experiment.extra_outcome_context}
            onChange={(e) => updateField("extra_outcome_context", e.target.value)}
            placeholder="Additional context for outcome tracking"
          />
        </div>
        <DebugConfigEditor
          value={experiment.debug_config}
          onChange={(v) => updateField("debug_config", v)}
        />
      </div>

      <Separator />

      <div className="space-y-4">
        <SectionHeader>Flags</SectionHeader>
        <div className="grid grid-cols-3 gap-3">
          {[
            { key: "is_template" as const, label: "Template" },
            { key: "is_automated_change" as const, label: "Automated Change" },
            { key: "suppress_editor_mention" as const, label: "Suppress Mentions" },
            { key: "enable_edit_raw_json_ui" as const, label: "Edit Raw JSON" },
            { key: "allocate_right_to_left" as const, label: "Alloc Right-to-Left" },
            { key: "is_managed" as const, label: "Managed" },
          ].map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer">
              <Switch
                checked={experiment[key]}
                onCheckedChange={(v) => updateField(key, v)}
              />
              <span className="text-xs text-subtext0">{label}</span>
            </label>
          ))}
        </div>
      </div>

      <Separator />

      <div className="space-y-4">
        <SectionHeader>Tags & Metadata</SectionHeader>
        <TagInput
          label="Field Numbers to Copy"
          tags={experiment.field_numbers_to_copy.map(String)}
          onChange={(v) => updateField("field_numbers_to_copy", v.map(Number))}
          placeholder="field number"
          inputType="number"
          inputClassName="w-32"
        />
        <TagInput
          label="Engine Feature Flags"
          tags={experiment.engine_feature_flags}
          onChange={(v) => updateField("engine_feature_flags", v)}
          placeholder="flag name"
          inputClassName="w-48"
        />
        <TagInput
          label="Growthbook Tags"
          tags={experiment.growthbook_tags}
          onChange={(v) => updateField("growthbook_tags", v)}
          placeholder="tag"
          inputClassName="w-48"
        />
      </div>
    </div>
  );

  const renderVariations = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs text-overlay1">
            {experiment.variations.length} variation{experiment.variations.length !== 1 ? "s" : ""}
          </span>
          {experiment.variations.length > 0 && (
            <span className="text-[10px] text-overlay0">
              &middot; total alloc: {experiment.variations.reduce((s, v) => s + v.target_allocation, 0)}%
            </span>
          )}
        </div>
        <Button type="button" variant="outline" size="sm" onClick={addVariation}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add Variation
        </Button>
      </div>

      {experiment.variations.length === 0 ? (
        <EmptyState
          icon={Variable}
          title="No variations"
          description="Add variations to define how users are split in this experiment."
        />
      ) : (
        <>
          <div className="p-3 rounded-lg border border-surface1 bg-surface0/50">
            <span className="text-[10px] font-medium text-overlay1 uppercase tracking-wider mb-2 block">
              Allocation
            </span>
            <div className="h-3 bg-surface1 rounded-full overflow-hidden flex">
              {experiment.variations.map((v, vi) => {
                const hue = 270 + vi * 35;
                return (
                  <div
                    key={vi}
                    className="h-full flex items-center justify-center text-[8px] font-bold text-crust transition-all"
                    style={{
                      width: `${Math.max(0, v.target_allocation)}%`,
                      backgroundColor: `hsl(${hue % 360}, 65%, 72%)`,
                    }}
                  >
                    {v.target_allocation > 8 ? `${v.target_allocation}%` : ""}
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
              {experiment.variations.map((v, vi) => {
                const hue = 270 + vi * 35;
                return (
                  <div key={vi} className="flex items-center gap-1.5 text-[10px] text-overlay0">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: `hsl(${hue % 360}, 65%, 72%)` }}
                    />
                    V{vi + 1}: {v.label || `Variation ${vi + 1}`}
                  </div>
                );
              })}
            </div>
          </div>

          {experiment.variations.map((variation, vi) => (
            <VariationEditor
              key={vi}
              variation={variation}
              index={vi}
              onChange={(v) => updateVariation(vi, v)}
              onRemove={() => removeVariation(vi)}
            />
          ))}
        </>
      )}
    </div>
  );

  const renderRules = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs text-overlay1">
          {experiment.rules.length} rule{experiment.rules.length !== 1 ? "s" : ""}
        </span>
        <Button type="button" variant="outline" size="sm" onClick={addRule}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add Rule
        </Button>
      </div>

      {experiment.rules.length === 0 ? (
        <EmptyState
          icon={Layers}
          title="No rules"
          description="Add rules to control which users are eligible for this experiment."
        />
      ) : (
        experiment.rules.map((rule, ri) => (
          <RuleEditor
            key={ri}
            rule={rule}
            index={ri}
            onChange={(r) => updateRule(ri, r)}
            onRemove={() => removeRule(ri)}
          />
        ))
      )}
    </div>
  );

  return (
    <div className="h-screen flex overflow-hidden bg-base">
      {/* Left panel */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="flex items-center justify-between px-5 py-3 border-b border-surface1 bg-mantle">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-mauve/25">
              <FlaskConical className="h-4 w-4 text-mauve" />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-text leading-none">Experiment Editor</h1>
              <p className="text-[11px] text-overlay0 mt-0.5">
                v{experiment.version} &middot; rev {experiment.revision}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {errorCount > 0 && (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-red/20 text-red text-xs">
                <AlertCircle className="h-3 w-3" />
                {errorCount} error{errorCount !== 1 ? "s" : ""}
              </div>
            )}
            <Button type="button" variant="outline" size="sm" onClick={validate} className="text-xs">
              Validate
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={reinitialize} className="text-xs">
              Reset
            </Button>
          </div>
        </header>

        {/* Tabs */}
        <div className="flex items-center border-b border-surface1 bg-mantle px-4 gap-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                className={`relative flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-colors ${
                  isActive
                    ? "text-mauve"
                    : "text-overlay0 hover:text-subtext0"
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon className="h-3.5 w-3.5" />
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && (
                  <span className={`ml-0.5 text-[10px] px-1.5 py-0.5 rounded-full ${
                    isActive ? "bg-mauve/30 text-mauve" : "bg-surface0 text-overlay1"
                  }`}>
                    {tab.count}
                  </span>
                )}
                {isActive && (
                  <div className="absolute bottom-0 left-3 right-3 h-[2px] bg-mauve rounded-full" />
                )}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <ScrollArea className="flex-1">
          <div className="p-5 max-w-3xl">
            {activeTab === "basic" && renderBasicInfo()}
            {activeTab === "config" && renderConfig()}
            {activeTab === "variations" && renderVariations()}
            {activeTab === "rules" && renderRules()}
          </div>
        </ScrollArea>
      </div>

      {/* Resize handle */}
      {showJson && (
        <>
          <div
            className="w-1 bg-surface1 cursor-col-resize hover:bg-mauve/70 transition-colors shrink-0"
            onMouseDown={() => setIsResizing(true)}
          />
          <div
            className="shrink-0 bg-mantle border-l border-surface1"
            style={{ width: jsonPanelWidth }}
          >
            <JsonPreview
              experiment={experiment}
              onChange={setExperiment}
              onImport={handleImport}
            />
          </div>
        </>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
}
